import type { FeedbackCategory } from "../../data/feedbackData";

type Cat = FeedbackCategory;

export const PILLAR_FEEDBACKS_EN: Record<number, Record<Cat, string>> = {
  1: {
    bas: "Your marketing strategy is flying blind — which has the advantage of adventure, but rarely ROI. Let's see if your brand has taken the lead.",
    mid: "The strategic foundations are there, with some grey areas. Let's see if your brand builds on them, or operates in parallel.",
    haut: "Your strategy is more structured than most B2B companies. The pressure is now on execution: is your brand keeping up?",
  },
  2: {
    bas: "Your brand and tools aren't doing the work for you yet. Every client interaction takes more effort than it should.",
    mid: "Your brand has foundations, without yet being a growth lever. The potential is there, the consistency is missing.",
    haut: "Your brand is an asset. Consistent identity, tools in place, every marketing action carries further.",
  },
  3: {
    bas: "On content and visibility, it's all quiet. Your competitors are capturing the attention you're not contesting.",
    mid: "Content and visibility, but not yet the rhythm or precision that turns attention into pipeline.",
    haut: "Your content machine is running. Regular production, active distribution, coordinated presence — it's an engine.",
  },
  4: {
    bas: "On AI and tools, you're starting from scratch. But this is also the pillar that progresses fastest when you really commit.",
    mid: "A few tools, a few AI use cases, but no system yet. You understand the stakes — it remains to be structured.",
    haut: "Your stack is in place and AI is working for you. It's a competitive edge that few B2B teams have mastered yet.",
  },
};

export const CROSS_P2_EN: Record<Cat, Record<Cat, string>> = {
  bas: {
    bas: "Neither strategy nor brand is carrying your marketing — it's moving forward by sheer effort. Common in B2B, and this is the terrain where quick wins are most visible.",
    mid: "Your brand is ahead of your strategy. The foundations exist, what's missing is the flight plan to exploit them.",
    haut: "Your brand is significantly more mature than your strategy. A great shop window, but without a traffic plan, visitors admire without necessarily acting.",
  },
  mid: {
    bas: "Your strategy is ahead of your brand. You know where to go but your image and tools aren't keeping up yet.",
    mid: "Strategy and brand are advancing at the same pace, in a balanced way. It's stable — let's see if your content makes the difference.",
    haut: "Your brand is stronger than your strategy. The means to execute are there, what remains is to refine the direction to exploit them fully.",
  },
  haut: {
    bas: "Sharp contrast: the strategy is solid, the brand isn't keeping up. Like an excellent product in packaging that doesn't do it justice.",
    mid: "Strategy leads, brand follows. The gap isn't dramatic: a few adjustments to image and tools can unlock a lot.",
    haut: "Strategy and brand are aligned: a rare combination in B2B. The pressure is now on your content and visibility.",
  },
};

export const CROSS_P3_UNIFORME_EN: Record<Cat, string> = {
  bas: "Three pillars, same observation: marketing is a work in progress. Paradoxically, it's a good starting point: no imbalance to correct, just a foundation to build. Last theme coming up.",
  mid: "Three pillars at the same intermediate level. No glaring gap, no distinctive strength. Your marketing works, it just doesn't stand out yet. Last theme coming up.",
  haut: "Strategy, brand and content: all above the B2B average. That's rare. Let's see if your tools and AI usage complete the picture.",
};

export const CROSS_P3_EN_RETRAIT_EN: Record<
  "strategie" | "marque" | "contenu",
  string
> = {
  strategie:
    "Execution (brand, content) is ahead of strategy. A common pattern: do first, structure later. The risk is lots of activity without convergence.",
  marque:
    "Strategy and content are moving, but the infrastructure (brand, tools, website) isn't keeping up. A classic B2B bottleneck: everything seems to work, nothing scales.",
  contenu:
    "Strategy and brand are solid, but visibility isn't following. Your marketing exists internally but is almost invisible from the outside. Good news: this is the pillar that progresses fastest.",
};

export const CROSS_P3_EN_AVANCE_EN: Record<
  "strategie" | "marque" | "contenu",
  string
> = {
  strategie:
    "Strategy is your strong point, execution is lagging behind. Clear vision, limited or poorly aligned resources. The last theme will tell whether the tools can bridge the gap.",
  marque:
    "Your infrastructure (brand, tools, website) is ahead of the rest. The foundations are solid, what's missing is the strategic direction or content to exploit them.",
  contenu:
    "Your content leads the way, but neither strategy nor infrastructure is at the same level. Being visible is good — but attention still needs to convert.",
};
